import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import LiveaboardOperatorDealsCard from './LiveaboardOperatorDealsCard'
import ResortDealsCard from './ResortDealsCard'
import setQuery from 'set-query-string'
import Pagination from 'react-js-pagination'
import { convertCurrency, Loader } from '../helpers'
import API from 'api'
import { parseQuery, roundPrice } from 'utils'
import { trackEcommerce, pushToDataLayer } from 'gtm'
import { FormattedMessage } from 'react-intl'
import { formatDate } from '../helpers/DateFormat'

let controller
let signal

const PAGE_SIZE = 12
const dealsContainer = document.getElementById('deals-list')
const dateFormat = 'DD MMM'

export default function DealsList({ lastMinuteDeals }) {
  const isInitialMount = useRef()
  const query = parseQuery()
  const [data, setData] = useState({
    results: [],
  })
  const [isLoading, setLoading] = useState(true)
  const [page, setPage] = useState(query.page ? parseInt(query.page, 10) : 1)
  const filters = useSelector((state) => state.filters)
  const pageType = useSelector((state) => state.pageType)
  const isDiveResort = pageType === 'dr_deals'
  let eCommerceListName = isDiveResort
    ? 'Travel Deals - Dive Resort'
    : 'Travel Deals - Liveaboard'

  if(lastMinuteDeals) {
    eCommerceListName = 'Travel Deals - Last Minute'
  }
  const onPageChange = (currentPage, preventScroll) => {
    if(!preventScroll) {
      window.scrollTo(0, dealsContainer ? dealsContainer.offsetTop : 0)
    }
    setPage(currentPage)
    setQuery({ page: currentPage })
  }

  useEffect(() => {
    if(isInitialMount.current) {
      onPageChange(1, true)
      getItems()
    }
    return () => controller.abort()
  }, [filters, pageType])

  useEffect(() => {
    getItems()
    return () => controller.abort()
  }, [page])

  function getItems() {
    if('AbortController' in window) {
      controller = new window.AbortController()
      signal = controller.signal
    }
    let url = isDiveResort ? 'promotions/resorts/' : 'promotions/'

    if(lastMinuteDeals) {
      url = 'promotions/last-minute/'
    }
    isInitialMount.current = true
    setLoading(true)

    API(url, signal)
      .get({
        ...filters,
        page_size: PAGE_SIZE,
        page,
        shop_type: pageType ? (pageType === 'dr_deals' ? 20 : 10) : undefined,
      })
      .then((data) => {
        const impressions = []
        data.results.forEach((item, index) => {
          const { promotion, dateFrom, dateTo, shopTitle, title } = item
          const dateFromFormatted = formatDate(dateFrom, dateFormat, 'en')
          const dateToFormatted = formatDate(dateTo, dateFormat, 'en')

          item.impression = {
            name: shopTitle,
            id: item.shopId,
            price: roundPrice(
              convertCurrency(item.price, item.currency, 'USD')
            ),
            brand: `${shopTitle}|${item.affiliateId || 'Non-PADI Operator'}`,
            category:
              isDiveResort || item.shopKind === 20
                ? 'Dive Resort'
                : 'Liveaboard',
            variant: `${
              promotion ? promotion.title : title || 'Deals'
            } / ${dateFromFormatted} - ${dateToFormatted}`,
            position: index + 1,
          }
          impressions.push({
            ...item.impression,
            list: eCommerceListName,
          })
        })

        trackEcommerce('impression', {
          currencyCode: 'USD',
          impressions,
        })
        setData(data)
      })
      .catch((e) => {
        if(e.status === 404) {
          onPageChange(1, true)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const trackEcommerceClick = (item) => {
    if(item.impression) {
      pushToDataLayer({
        event: 'productClick',
        ecommerce: {
          currencyCode: 'USD',
          click: {
            actionField: {
              list: eCommerceListName,
            },
            products: [item.impression],
          },
        },
      })
    }
  }
  const noResults = !isLoading && !data.results.length

  return (
    <section
      className={`promotion-wrapper ${isLoading ? 'loading' : ''} ${
        noResults ? 'empty' : ''
      }`}
    >
      <Loader />
      {noResults && (
        <div className='no-results'>
          <FormattedMessage id='no_results' defaultMessage='No results' />
        </div>
      )}
      {data.results.map((item) =>
        isDiveResort || item.shopKind === 20 ? (
          <ResortDealsCard
            key={item.url}
            shop={item}
            lastMinuteDeals={lastMinuteDeals}
            handleClick={trackEcommerceClick}
          />
        ) : (
          <LiveaboardOperatorDealsCard
            key={item.url}
            shop={item}
            lastMinuteDeals={lastMinuteDeals}
            handleClick={trackEcommerceClick}
          />
        )
      )}
      {data.count > PAGE_SIZE && (
        <div className='pagination-wrap'>
          <Pagination
            activePage={page}
            innerClass='custom-pagination'
            itemsCountPerPage={PAGE_SIZE}
            itemClassPrev='prev'
            itemClassNext='next'
            prevPageText=''
            nextPageText=''
            itemClassFirst='hidden'
            itemClassLast='hidden'
            totalItemsCount={data.count}
            pageRangeDisplayed={5}
            onChange={onPageChange}
          />
        </div>
      )}
    </section>
  )
}
